import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Search } from "lucide-react";
import {
  Body,
  Caption,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VDataTable,
  VInput,
  VKpiCard,
  VSelect,
  type VDataTableColumn,
} from "@/components/ui";
import { useCases } from "@/domains/cases/api/queries";
import type { CaseFile, CaseStatus } from "@/domains/cases/api/types";
import { CaseStatusBadge } from "@/domains/cases/components/caseStatusBadge";

const STATUS_OPTIONS: ReadonlyArray<{ value: CaseStatus | "all"; label: string }> = [
  { value: "all", label: "Alle statussen" },
  { value: "intake", label: "Intake" },
  { value: "scheduled", label: "Ingepland" },
  { value: "active", label: "Lopend" },
  { value: "closing", label: "Afsluiten" },
  { value: "closed", label: "Afgesloten" },
];

function parseStatusFilter(value: string): CaseStatus | "all" {
  return STATUS_OPTIONS.find((o) => o.value === value)?.value ?? "all";
}

export function CaseListPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { data, isPending, isError } = useCases();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<CaseStatus | "all">("all");
  const [managerId, setManagerId] = useState<string>("all");

  const managers = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of data ?? []) {
      if (!seen.has(c.managerId)) seen.set(c.managerId, c.managerName);
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const filtered = useMemo<ReadonlyArray<CaseFile>>(() => {
    const list = data ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((c) => {
      if (status !== "all" && c.status !== status) return false;
      if (managerId !== "all" && c.managerId !== managerId) return false;
      if (!q) return true;
      return (
        c.number.toLowerCase().includes(q) ||
        c.title.toLowerCase().includes(q) ||
        c.address.toLowerCase().includes(q)
      );
    });
  }, [data, search, status, managerId]);

  const stats = useMemo(() => {
    const list = data ?? [];
    return {
      active: list.filter((c) => c.status === "active").length,
      scheduled: list.filter((c) => c.status === "scheduled").length,
      intake: list.filter((c) => c.status === "intake").length,
      closed: list.filter((c) => c.status === "closed").length,
    };
  }, [data]);

  const columns: ReadonlyArray<VDataTableColumn<CaseFile>> = [
    {
      key: "number",
      header: "Dossier",
      cell: (c) => <span className="font-semibold">#{c.number}</span>,
    },
    {
      key: "title",
      header: "Titel",
      cell: (c) => (
        <div>
          <Body weight="medium">{c.title}</Body>
          <Caption tone="muted">{c.address}</Caption>
        </div>
      ),
    },
    {
      key: "period",
      header: "Periode",
      cell: (c) => (
        <Caption tone="default">
          {c.startDate} → {c.endDate}
        </Caption>
      ),
    },
    {
      key: "assets",
      header: "Assets",
      cell: (c) => <Caption tone="default">{c.assetIds.length}</Caption>,
      align: "center",
    },
    {
      key: "manager",
      header: "Behandelaar",
      cell: (c) => <Caption tone="default">{c.managerName}</Caption>,
    },
    {
      key: "status",
      header: "Status",
      cell: (c) => <CaseStatusBadge status={c.status} />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dossiers"
        subtitle="Alle camera-inzet dossiers — een dossier koppelt 1 aanvraag aan 1 of meer assets, taken en communicatie."
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <VKpiCard label="Lopend" value={stats.active} helper="Camera's op locatie" />
        <VKpiCard label="Ingepland" value={stats.scheduled} helper="Wacht op plaatsing" />
        <VKpiCard label="Intake" value={stats.intake} helper="Net binnengekomen" />
        <VKpiCard label="Afgesloten" value={stats.closed} helper="Gearchiveerd" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full min-w-[240px] max-w-sm flex-1 sm:w-auto">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <VInput
            placeholder="Zoek op dossiernummer, titel of adres…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="w-[200px]">
          <VSelect
            value={status}
            onChange={(e) => setStatus(parseStatusFilter(e.target.value))}
            aria-label="Filter op status"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </VSelect>
        </div>
        <div className="w-[200px]">
          <VSelect
            value={managerId}
            onChange={(e) => setManagerId(e.target.value)}
            aria-label="Filter op behandelaar"
          >
            <option value="all">Alle behandelaars</option>
            {managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </VSelect>
        </div>
      </div>

      {isPending ? (
        <LoadingState label="Dossiers laden…" />
      ) : isError ? (
        <ErrorMessage message="Kon dossiers niet ophalen." />
      ) : (
        <VDataTable
          columns={columns}
          rows={filtered}
          getRowKey={(c) => c.id}
          onRowClick={(c) => navigate(`/cases/${c.id}`)}
          emptyState={<EmptyState title="Geen dossiers gevonden" />}
        />
      )}
    </div>
  );
}
